import { useQuery } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  type Timestamp,
} from 'firebase/firestore';
import { firebaseDb } from '../lib/firebase';
import type { BlogPostStatus } from '../content/blog/types';

/**
 * Firestore client hooks for the public blog.
 *
 * Read access is gated by the firestore.rules `status == 'published'` clause —
 * drafts/scheduled/archived posts are invisible to anonymous clients.
 */

export interface PublishedBlogPost {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  category: string;
  tags: string[];
  author: { name: string; role: string };
  coverImage: string;
  readingTime: string;
  publishedAt: Date | null;
  status: BlogPostStatus;
  canonicalUrl: string;
}

function tsToDate(ts: unknown): Date | null {
  if (!ts) return null;
  const t = ts as Timestamp;
  return typeof t.toDate === 'function' ? t.toDate() : null;
}

function docToPost(id: string, data: Record<string, unknown>): PublishedBlogPost {
  const author = (data['author'] ?? { name: '', role: '' }) as { name: string; role: string };
  return {
    slug: id,
    title: (data['title'] as string) ?? '',
    description: (data['description'] as string) ?? '',
    excerpt: (data['excerpt'] as string) ?? '',
    category: (data['category'] as string) ?? '',
    tags: (data['tags'] as string[]) ?? [],
    author,
    coverImage: (data['coverImage'] as string) ?? '',
    readingTime: (data['readingTime'] as string) ?? '',
    publishedAt: tsToDate(data['publishedAt']),
    status: ((data['status'] as BlogPostStatus) ?? 'draft'),
    canonicalUrl: (data['canonicalUrl'] as string) ?? '',
  };
}

/** List all published posts, newest first. */
export function usePublishedBlogPosts() {
  return useQuery<PublishedBlogPost[]>({
    queryKey: ['blog_posts', 'published'],
    queryFn: async () => {
      // Single-field where() — no composite index needed. Sort client-side
      // since the dataset is small (dozens of posts at most).
      const q = query(
        collection(firebaseDb, 'blog_posts'),
        where('status', '==', 'published'),
      );
      const snap = await getDocs(q);
      const posts = snap.docs.map((d) =>
        docToPost(d.id, d.data() as Record<string, unknown>),
      );
      return posts.sort((a, b) => {
        const ta = a.publishedAt?.getTime() ?? 0;
        const tb = b.publishedAt?.getTime() ?? 0;
        return tb - ta;
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Look up a single blog post by slug. Returns the document only if the
 * Firestore rule passes (status == 'published'). For any other state
 * (draft, scheduled, archived, missing), returns null — the dispatcher
 * treats null as 404.
 */
export function useBlogPostBySlug(slug: string | undefined) {
  return useQuery<PublishedBlogPost | null>({
    queryKey: ['blog_posts', 'doc', slug],
    enabled: !!slug,
    queryFn: async () => {
      if (!slug) return null;
      try {
        const ref = doc(firebaseDb, 'blog_posts', slug);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        return docToPost(snap.id, snap.data() as Record<string, unknown>);
      } catch {
        // Permission denied → treat as not published
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}
